<template>
  <div class="admin-page">
    <div class="admin-container">
      <header class="page-header">
        <div class="header-main">
          <h1>System Administration</h1>
          <p>Control center for users, requests, and system analytics.</p>
        </div>
        <div class="stats-summary" v-if="!loading">
          <div class="mini-stat">
            <span class="val">{{ requests.access.length }}</span>
            <span class="lbl">New Requests</span>
          </div>
          <div class="mini-stat">
            <span class="val">{{ requests.resets.length }}</span>
            <span class="lbl">Reset Tasks</span>
          </div>
        </div>
      </header>

      <div class="glass-card admin-card">
        <nav class="tabs-nav">
          <button :class="{ active: activeTab === 'access' }" @click="activeTab = 'access'">
            Access Requests
            <span v-if="requests.access.length" class="badge">{{ requests.access.length }}</span>
          </button>
          <button :class="{ active: activeTab === 'users' }" @click="activeTab = 'users'">
            User List
          </button>
          <button :class="{ active: activeTab === 'resets' }" @click="activeTab = 'resets'">
            Password Resets
          </button>
          <button :class="{ active: activeTab === 'analytics' }" @click="activeTab = 'analytics'">
            Analytics
          </button>
        </nav>

        <div class="tab-content">
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Fetching latest data...</p>
          </div>

          <!-- Access Requests -->
          <div v-else-if="activeTab === 'access'">
            <div v-if="requests.access.length" class="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Requester</th>
                    <th>Credentials</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="req in requests.access" :key="req.id">
                    <td>
                      <div class="user-meta">
                        <span class="name">{{ req.full_name }}</span>
                        <span class="email">{{ req.email }}</span>
                      </div>
                    </td>
                    <td>
                      <div class="creds">
                        <code>@{{ req.username }}</code>
                        <span class="req-pass" :title="req.requested_password">🔒 Requested Pass</span>
                      </div>
                    </td>
                    <td class="reason-cell">{{ req.reason }}</td>
                    <td><span class="status-pill pending">Pending</span></td>
                    <td>
                      <div class="actions">
                        <button @click="handleApprove(req.id)" class="btn-sm-primary">Approve</button>
                        <button @click="handleReject(req.id)" class="btn-text reject">Reject</button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">
              <div class="empty-icon">📂</div>
              <h3>No Pending Requests</h3>
              <p>Everything is caught up!</p>
            </div>
          </div>

          <!-- User List -->
          <div v-else-if="activeTab === 'users'">
            <div class="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Full Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Management</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in users" :key="user.id">
                    <td><strong>{{ user.full_name }}</strong></td>
                    <td><code>{{ user.username }}</code></td>
                    <td>{{ user.email }}</td>
                    <td><span :class="['role-tag', user.role.toLowerCase()]">{{ user.role }}</span></td>
                    <td>
                      <button @click="promptNewPassword(user)" class="btn-text">Change Password</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Password Resets -->
          <div v-else-if="activeTab === 'resets'">
            <div v-if="requests.resets.length" class="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Official Email</th>
                    <th>Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="req in requests.resets" :key="req.id">
                    <td><code>@{{ req.username }}</code></td>
                    <td>{{ req.email_kantor }}</td>
                    <td>{{ new Date(req.created_at).toLocaleString() }}</td>
                    <td>
                      <button @click="handleCompleteReset(req.id)" class="btn-sm-primary">Reset to password123</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">No pending reset requests.</div>
          </div>

          <!-- Analytics -->
          <div v-else-if="activeTab === 'analytics'" class="analytics-view">
            <div class="charts-grid">
              <div class="chart-card glass-card">
                <h3>Monthly Ticket Trends</h3>
                <div class="simple-bar-chart">
                  <div v-for="m in stats.monthly" :key="m.month" class="bar-item">
                    <div class="bar-container">
                      <div class="bar" :style="{ height: (m.count * 10) + 'px' }"></div>
                    </div>
                    <span class="bar-label">{{ m.month }}</span>
                  </div>
                </div>
              </div>
              <div class="chart-card glass-card">
                <h3>Priority Distribution</h3>
                <div class="priority-stats">
                  <div v-for="p in stats.priority" :key="p.priority" class="p-stat">
                    <span class="p-label">{{ p.priority }}</span>
                    <div class="p-bar-wrap">
                       <div class="p-bar" :class="p.priority.toLowerCase()" :style="{ width: (p.count / totalTickets * 100) + '%' }"></div>
                    </div>
                    <span class="p-count">{{ p.count }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, reactive, computed } from 'vue'
import axios from 'axios'

const API_URL = (import.meta.env.VITE_API_URL || 'https://it-ticketing-api.maulanarofiq72.workers.dev').replace(/\/$/, '')

const activeTab = ref('access')
const loading = ref(true)
const users = ref<any[]>([])
const requests = reactive({
  access: [] as any[],
  resets: [] as any[]
})
const stats = reactive({
  monthly: [] as any[],
  priority: [] as any[]
})

const totalTickets = computed(() => stats.priority.reduce((a, b) => a + b.count, 0) || 1)

const fetchData = async () => {
  loading.value = true
  try {
    const [reqs, userList, statData] = await Promise.all([
      axios.get(`${API_URL}/admin/requests`),
      axios.get(`${API_URL}/admin/users`),
      axios.get(`${API_URL}/admin/stats`)
    ])
    requests.access = reqs.data.access
    requests.resets = reqs.data.resets
    users.value = userList.data
    stats.monthly = statData.data.monthly
    stats.priority = statData.data.priority
  } catch (err) {
    console.error('Admin Fetch Error', err)
  } finally {
    loading.value = false
  }
}

const handleApprove = async (id: number) => {
  try {
    await axios.post(`${API_URL}/admin/approve-access/${id}`)
    await fetchData()
  } catch (err) { alert('Approval failed') }
}

const handleReject = async (id: number) => {
  if (!confirm('Reject request?')) return
  try {
    await axios.post(`${API_URL}/admin/reject-access/${id}`)
    await fetchData()
  } catch (err) { alert('Rejection failed') }
}

const handleCompleteReset = async (id: number) => {
  try {
    await axios.post(`${API_URL}/admin/complete-reset/${id}`)
    await fetchData()
  } catch (err) { alert('Reset failed') }
}

const promptNewPassword = async (user: any) => {
  const newPass = prompt(`Enter new password for ${user.username}:`)
  if (newPass) {
    try {
      await axios.post(`${API_URL}/admin/users/${user.id}/password`, { password: newPass })
      alert('Password updated successfully')
    } catch (err) { alert('Update failed') }
  }
}

onMounted(fetchData)
</script>

<style scoped>
.admin-page { padding: 2rem; max-width: 1400px; margin: 0 auto; }

.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; }
.header-main h1 { font-size: 2rem; color: var(--primary); margin-bottom: 0.25rem; font-weight: 800; }
.header-main p { color: var(--text-muted); }

.stats-summary { display: flex; gap: 1.5rem; }
.mini-stat {
  background: white; padding: 0.75rem 1.25rem; border-radius: 12px;
  border: 1px solid var(--border); text-align: center;
}
.mini-stat .val { display: block; font-size: 1.5rem; font-weight: 800; color: var(--accent); }
.mini-stat .lbl { font-size: 0.75rem; color: var(--text-muted); font-weight: 600; text-transform: uppercase; }

.admin-card { overflow: hidden; }

.tabs-nav { display: flex; background: #fdfdfd; border-bottom: 1px solid var(--border); }
.tabs-nav button {
  padding: 1.25rem 1.5rem; border: none; background: none; font-weight: 600;
  color: var(--text-muted); cursor: pointer; display: flex; align-items: center; gap: 0.5rem;
  transition: all 0.2s; position: relative; font-size: 0.95rem;
}
.tabs-nav button.active { color: var(--primary); background: white; }
.tabs-nav button.active::after {
  content: ''; position: absolute; bottom: -1px; left: 0; right: 0;
  height: 2px; background: var(--primary);
}
.badge { background: #ef4444; color: white; font-size: 0.7rem; padding: 0.1rem 0.4rem; border-radius: 10px; font-weight: 800; }

.tab-content { padding: 0; }
.table-wrapper { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; text-align: left; }
th { background: #fdfdfd; padding: 1rem 1.5rem; font-size: 0.8rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; }
td { padding: 1.25rem 1.5rem; border-top: 1px solid var(--border); color: var(--text-main); }

.user-meta .name { display: block; font-weight: 700; }
.user-meta .email { font-size: 0.85rem; color: var(--text-muted); }

.creds code { background: #f1f5f9; padding: 0.25rem 0.5rem; border-radius: 6px; color: var(--accent); font-family: monospace; font-weight: 600; }
.req-pass { display: block; font-size: 0.75rem; color: var(--text-muted); margin-top: 0.25rem; font-style: italic; }

.status-pill { padding: 0.4rem 1rem; border-radius: 20px; font-size: 0.75rem; font-weight: 700; text-transform: capitalize; }
.status-pill.pending { background: #fffbeb; color: #d97706; }

.role-tag { padding: 0.2rem 0.6rem; border-radius: 6px; font-size: 0.7rem; font-weight: 800; text-transform: uppercase; }
.role-tag.admin { background: #e0f2fe; color: #0369a1; }
.role-tag.agent { background: #fef3c7; color: #92400e; }
.role-tag.user { background: #f1f5f9; color: #475569; }

.actions { display: flex; gap: 1rem; align-items: center; }
.btn-sm-primary { background: var(--primary); color: white; border: none; padding: 0.5rem 1rem; border-radius: 8px; font-weight: 600; cursor: pointer; }
.btn-text { background: none; border: none; color: var(--accent); font-weight: 600; cursor: pointer; text-decoration: none; padding: 0; }
.btn-text.reject { color: #ef4444; }

.empty-state { text-align: center; padding: 5rem 2rem; }
.empty-icon { font-size: 3rem; margin-bottom: 1rem; }
.empty-state h3 { color: var(--primary); margin-bottom: 0.5rem; }

.charts-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; padding: 2rem; }
.chart-card { padding: 1.5rem; }
.chart-card h3 { font-size: 1.1rem; margin-bottom: 1.5rem; color: var(--primary); }

.simple-bar-chart { display: flex; align-items: flex-end; gap: 1rem; height: 150px; padding-top: 1rem; }
.bar-item { flex: 1; display: flex; flex-direction: column; align-items: center; }
.bar-container { width: 24px; height: 100px; display: flex; align-items: flex-end; }
.bar { width: 100%; background: var(--accent); border-radius: 4px 4px 0 0; }
.bar-label { font-size: 0.65rem; color: var(--text-muted); margin-top: 0.5rem; transform: rotate(-45deg); font-weight: 600; }

.priority-stats { display: flex; flex-direction: column; gap: 1.25rem; }
.p-stat { display: grid; grid-template-columns: 80px 1fr 30px; align-items: center; gap: 1rem; }
.p-label { font-size: 0.75rem; font-weight: 700; color: var(--text-main); }
.p-bar-wrap { background: #f1f5f9; height: 10px; border-radius: 5px; overflow: hidden; }
.p-bar { height: 100%; border-radius: 5px; }
.p-bar.critical { background: #ef4444; }
.p-bar.high { background: #f59e0b; }
.p-bar.medium { background: #10b981; }
.p-bar.low { background: #3b82f6; }
.p-count { font-weight: 800; color: var(--text-main); font-size: 0.85rem; }

.loading-state { text-align: center; padding: 5rem; }
.spinner { width: 40px; height: 40px; border: 4px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 1rem; }
</style>
